// @flow
import { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import reduce from 'ramda/es/reduce';
import isEmpty from 'ramda/es/isEmpty';
import values from 'ramda/es/values';
import assocPath from 'ramda/es/assocPath';
import filter from 'ramda/es/filter';
import propEq from 'ramda/es/propEq';
import complement from 'ramda/es/complement';
import map from 'ramda/es/map';


const propNotEq = complement(propEq);

export type ProcessingFileType = {
  id: string,
  file: File,
  name: string,
  type: string,
  uploadedPercent: number, // процент загрузки. Если -1 то загрузка не удалась
  processing: boolean, // в процессе загрузки
}

export type ProcessingFilesType = {
  [id: string]: ProcessingFileType
}


const defaultState = {};
const processFiles = (initialPercent: number) => (acc, incommingFile): ProcessingFileType => {
  const { name, type } = incommingFile;
  const id = uuid();
  return {
    ...acc,
    [id]: {
      id,
      file: incommingFile,
      name,
      type,
      uploadedPercent: initialPercent,
      processing: false,
    },
  };
};

export default function useUploadingFiles(
  uploadFile: (
    file: File,
    solutionId: number,
    onProgress: (progressEvent: { loaded: number, total: number}) => void,
    onSuccess: () => void,
    onFail: () => void,
  ) => Promise<boolean>,
  solutionId: ?number
): any {
  const [filesProcessing, setFilesProcessing] = useState<ProcessingFilesType>(defaultState);
  const [filesFailed, setFilesFailed] = useState(defaultState);
  const [uploading, setUploading] = useState(false);


  // const handleDeleteAllFailed = useCallback(() => {
  //   setFilesProcessing(filter(propNotEq('uploadedPercent', -1)));
  //   setFilesFailed(defaultState);
  // }, []);


  const handleProgress = useCallback((fileId: string) =>
    (progressEvent: { loaded: number, total: number }) => {
      const { loaded, total } = progressEvent;
      const percents = Math.round(loaded / (total / 100.0));
      setFilesProcessing(assocPath([fileId, 'uploadedPercent'], percents));
    }, []);


  const handleLoadSucces = useCallback((fileId: string) => () => {
    setFilesProcessing(filter(propNotEq('id', fileId)));
  }, []);

  const handleLoadFail = useCallback((fileId: string) => () => {
    setFilesProcessing(assocPath([fileId, 'uploadedPercent'], -1));
  }, []);


  const handleUploadFiles = useCallback((accepted: Array<File>, rejected: Array<File>) => {
    const acceptedFiles: ProcessingFilesType = reduce(processFiles(0), {}, accepted);
    const rejectedFiles: ProcessingFilesType = reduce(processFiles(-1), {}, rejected);
    setFilesProcessing(acceptedFiles);
    setFilesFailed(rejectedFiles);
  }, []);

  const handleDeleteFile = useCallback((fileId: string) => {
    if (filesProcessing[fileId]) {
      setFilesProcessing(filter(propNotEq('id', fileId)));
    }
    if (filesFailed[fileId]) {
      setFilesFailed(filter(propNotEq('id', fileId)));
    }
  }, [filesProcessing, filesFailed]);


  const filesNeedToLoad = useMemo(() => filter(
    ({ processing, uploadedPercent }) => (!processing && uploadedPercent === 0),
    values(filesProcessing)
  ), [filesProcessing]);


  useEffect(() => () => {
    setFilesProcessing(defaultState);
    setFilesFailed(defaultState);
  }, [solutionId]);


  useEffect(() => {
    if (isEmpty(filesNeedToLoad) || !solutionId || uploading) {
      return;
    }

    setFilesProcessing((state) => {
      const newState = map((item) => {
        const { processing, uploadedPercent } = item;
        if (!processing && uploadedPercent === 0) {
          return { ...item, processing: true };
        }
        return item;
      }, state);
      return newState;
    });

    (async () => {
      setUploading(true);
      await Promise.all(filesNeedToLoad.map(
        ({ id, file }) => uploadFile(
          file,
          solutionId,
          handleProgress(id),
          handleLoadSucces(id),
          handleLoadFail(id)
        )
      ));
      setUploading(false);
    })();
  }, [filesNeedToLoad]);

  return [filesProcessing, filesFailed, handleUploadFiles, handleDeleteFile, uploading];
}
